import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'

import { AdminLayout } from '../../../layouts/admin-layout'
import { Badge } from '../../../components/ui/badge'
import { Input } from '../../../components/ui/input'
import { Button } from '../../../components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../../../components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
}

interface Organization {
  id: number
  name: string
  current_plan: string
}

interface Project {
  id: number
  name: string
  is_archived: boolean
  created_at: string
  updated_at: string
  author_email: string
  user: User
  organization?: Organization
  // Activity metrics
  credits_used: number
  message_count: number
  conversation_count: number
  last_activity_at: string | null
}

interface Filters {
  plan: string
  sort: string | null
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface AdminProjectsIndexProps {
  projects: Project[]
  pagy: Pagination
  filters: Filters
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function Index({ projects, pagy, filters }: AdminProjectsIndexProps) {
  const { t } = useTranslation('admin')
  const [searchName, setSearchName] = useState('')
  const [searchUserEmail, setSearchUserEmail] = useState('')

  const buildParams = (overrides: Record<string, string | null> = {}) => {
    const params: Record<string, string> = {}

    // Preserve search filters
    if (searchName) params['q[name_cont]'] = searchName
    if (searchUserEmail) params['q[author_email_cont]'] = searchUserEmail

    // Preserve current filters
    if (filters.plan) params['plan'] = filters.plan
    if (filters.sort) params['sort'] = filters.sort

    // Apply overrides
    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }

    return params
  }

  const handleSearch = () => {
    const params: Record<string, string> = {}

    if (searchName) params['q[name_cont]'] = searchName
    if (searchUserEmail) params['q[author_email_cont]'] = searchUserEmail
    if (filters.plan) params['plan'] = filters.plan
    if (filters.sort) params['sort'] = filters.sort

    router.get('/admin/projects', params, { preserveState: true })
  }

  const handleReset = () => {
    setSearchName('')
    setSearchUserEmail('')
    // Reset to defaults: paid projects only, default sort
    router.get('/admin/projects', { plan: 'paid' }, { preserveState: true })
  }

  const handlePlanChange = (value: string) => {
    router.get('/admin/projects', buildParams({ plan: value }), { preserveState: true })
  }

  const handleSortChange = (value: string) => {
    const sortValue = value === 'default' ? null : value
    router.get('/admin/projects', buildParams({ sort: sortValue }), { preserveState: true })
  }

  const handleSort = (field: string) => {
    const params = new URLSearchParams(window.location.search)
    const currentSort = params.get('q[s]')

    // Toggle between asc and desc
    let newSort = `${field} asc`
    if (currentSort === `${field} asc`) {
      newSort = `${field} desc`
    }

    const searchParams: Record<string, string> = {}

    // Preserve existing search filters
    if (searchName) searchParams['q[name_cont]'] = searchName
    if (searchUserEmail) searchParams['q[author_email_cont]'] = searchUserEmail
    if (filters.plan) searchParams['plan'] = filters.plan

    // Add sort parameter
    searchParams['q[s]'] = newSort

    router.get('/admin/projects', searchParams, { preserveState: true })
  }

  const getSortIcon = (field: string) => {
    const params = new URLSearchParams(window.location.search)
    const currentSort = params.get('q[s]')

    if (currentSort === `${field} asc`) {
      return '↑'
    } else if (currentSort === `${field} desc`) {
      return '↓'
    }
    return '⇅'
  }

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return t('projects.index.relativeTime.never')
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return t('projects.index.relativeTime.today')
    if (diffDays === 1) return t('projects.index.relativeTime.yesterday')
    if (diffDays < 7) return t('projects.index.relativeTime.daysAgo', { count: diffDays })
    if (diffDays < 30) return t('projects.index.relativeTime.weeksAgo', { count: Math.floor(diffDays / 7) })
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // Calculate max credits for progress bar scaling
  const getCredits = (val: number | string | null | undefined) => Number(val) || 0
  const maxCredits = Math.max(...projects.map(p => getCredits(p.credits_used)), 1)

  return (
    <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col py-2">
            <div className="px-3">
              {/* Compact Filter Bar */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm font-semibold whitespace-nowrap">
                  {t('projects.index.titleWithCount', { count: pagy.count })}
                </span>
                <div className="w-px h-4 bg-border" />
                <Select value={filters.plan || 'paid'} onValueChange={handlePlanChange}>
                  <SelectTrigger className="h-7 text-xs w-28">
                    <SelectValue placeholder={t('projects.index.table.plan')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">{t('projects.index.filters.plan.paidOnly')}</SelectItem>
                    <SelectItem value="free">{t('projects.index.filters.plan.free')}</SelectItem>
                    <SelectItem value="standard">{t('projects.index.filters.plan.standard')}</SelectItem>
                    <SelectItem value="pro">{t('projects.index.filters.plan.pro')}</SelectItem>
                    <SelectItem value="team">{t('projects.index.filters.plan.team')}</SelectItem>
                    <SelectItem value="all">{t('projects.index.filters.plan.all')}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.sort || 'default'} onValueChange={handleSortChange}>
                  <SelectTrigger className="h-7 text-xs w-36">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">{t('projects.index.filters.sort.default')}</SelectItem>
                    <SelectItem value="credits_desc">{t('projects.index.filters.sort.creditsDesc')}</SelectItem>
                    <SelectItem value="credits_asc">{t('projects.index.filters.sort.creditsAsc')}</SelectItem>
                    <SelectItem value="messages_desc">{t('projects.index.filters.sort.messagesDesc')}</SelectItem>
                    <SelectItem value="last_activity_desc">{t('projects.index.filters.sort.lastActivityDesc')}</SelectItem>
                  </SelectContent>
                </Select>
                <div className="w-px h-4 bg-border" />
                <Input
                  type="text"
                  placeholder={t('projects.index.filters.namePlaceholder')}
                  className="h-7 text-xs w-32"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Input
                  type="text"
                  placeholder={t('projects.index.filters.userEmailPlaceholder')}
                  className="h-7 text-xs w-36"
                  value={searchUserEmail}
                  onChange={(e) => setSearchUserEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} className="h-7 px-2 text-xs">
                  {t('projects.index.buttons.search')}
                </Button>
                <Button onClick={handleReset} variant="ghost" className="h-7 px-2 text-xs">
                  {t('projects.index.buttons.reset')}
                </Button>
              </div>

              {/* Projects Table */}
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)', minHeight: '400px' }}>
                  <table className="w-full">
                    <thead className="bg-muted sticky top-0 z-10">
                      <tr>
                        <th
                          className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            {t('projects.index.table.projectName')}
                            <span className="text-gray-400">{getSortIcon('name')}</span>
                          </div>
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('projects.index.table.plan')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('projects.index.table.owner')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('projects.index.table.status')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('projects.index.table.credits')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('projects.index.table.messages')}
                        </th>
                        <th
                          className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                          onClick={() => handleSort('last_activity_at')}
                        >
                          <div className="flex items-center gap-1">
                            {t('projects.index.table.activity')}
                            <span className="text-gray-400">{getSortIcon('last_activity_at')}</span>
                          </div>
                        </th>
                        <th
                          className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                          onClick={() => handleSort('created_at')}
                        >
                          <div className="flex items-center gap-1">
                            {t('projects.index.table.created')}
                            <span className="text-gray-400">{getSortIcon('created_at')}</span>
                          </div>
                        </th>
                        <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                          {t('projects.index.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {projects.length > 0 ? (
                        projects.map((project) => (
                          <tr key={project.id} className="hover:bg-muted transition-colors">
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm font-medium text-foreground">{project.name}</span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              {project.organization?.current_plan ? (
                                <Badge
                                  variant={project.organization.current_plan === 'free' ? 'secondary' : 'default'}
                                  className="text-xs px-1.5 py-0.5"
                                >
                                  {project.organization.current_plan.charAt(0).toUpperCase() + project.organization.current_plan.slice(1)}
                                </Badge>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">{project.author_email}</span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              {project.is_archived ? (
                                <Badge variant="warning" className="text-xs px-1.5 py-0.5">{t('projects.index.badges.archived')}</Badge>
                              ) : (
                                <Badge variant="success" className="text-xs px-1.5 py-0.5">{t('projects.index.badges.active')}</Badge>
                              )}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-medium text-foreground">
                                  {getCredits(project.credits_used).toFixed(1)}
                                </span>
                                <div className="w-12 h-1 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full"
                                    style={{ width: `${Math.min((getCredits(project.credits_used) / maxCredits) * 100, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">{project.message_count}</span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">{formatRelativeTime(project.last_activity_at)}</span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">
                                {new Date(project.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-2">
                                <a
                                  href={`/projects/${project.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-green-600 hover:text-green-800"
                                >
                                  {t('projects.index.table.open')}
                                </a>
                                <Link
                                  href={`/admin/projects/${project.id}`}
                                  className="text-sm text-blue-600 hover:text-blue-900"
                                >
                                  {t('projects.index.table.view')}
                                </Link>
                                {!project.is_archived && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <button className="text-sm text-amber-600 hover:text-amber-800">
                                        {t('projects.index.table.archive')}
                                      </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader className="items-center">
                                        <AlertDialogTitle>{t('projects.show.archiveDialog.title', { name: project.name })}</AlertDialogTitle>
                                        <AlertDialogDescription className="text-center">
                                          {t('projects.show.archiveDialog.description')}
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>{t('projects.show.archiveDialog.cancel')}</AlertDialogCancel>
                                        <AlertDialogAction
                                          className="bg-amber-600 hover:bg-amber-700 text-white"
                                          onClick={() => router.post(`/admin/projects/${project.id}/archive`)}
                                        >
                                          {t('projects.show.archiveDialog.confirm')}
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className="px-3 py-8 text-center text-sm text-muted-foreground">
                            {t('projects.index.empty')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Compact Pagination */}
                {pagy.pages > 1 && (
                  <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                    <span className="text-muted-foreground">
                      {pagy.from}-{pagy.to} of {pagy.count}
                    </span>
                    <div className="flex gap-1">
                      {pagy.prev && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => router.get('/admin/projects', { ...buildParams(), page: pagy.prev }, { preserveState: true })}
                        >
                          {t('projects.index.pagination.previous')}
                        </Button>
                      )}
                      {pagy.next && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => router.get('/admin/projects', { ...buildParams(), page: pagy.next }, { preserveState: true })}
                        >
                          {t('projects.index.pagination.next')}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  )
}




Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
